// src/components/AccountCreationForm.js
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { auth, db } from "../firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Button from './Button/Button';
import H2 from './H2/H2';
import MessagePopup from './MessagePopup/MessagePopup';

const USER_TYPES = {
  MIGRANTE_REFUGIADO: 'migrante_refugiado',
  REPRESENTANTE_ORGANIZACAO: 'representante_organizacao',
  APOIADOR: 'apoiador',
};

const SITUATION_KEYS = ['refugiado', 'solicitante_refugio', 'migrante', 'apatrida', 'outro'];

const sanitizeCnpj = (value = '') => value.replace(/\D/g, '');

const AccountCreationForm = () => {
  const { t } = useTranslation('auth');
  const [userType, setUserType] = useState(USER_TYPES.MIGRANTE_REFUGIADO);
  const [showMessage, setShowMessage] = useState(false);
  const [messageContent, setMessageContent] = useState({ title: '', message: '' });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    cnpj: '',
    institutionName: '',
    nationality: '',
    situation: '',
    country: 'Brasil',
    state: '',
    city: '',
    neighborhood: '',
    email: '',
    password: '',
    termsAgreed: false
  });
  const navigate = useNavigate();

  const isInstitution = userType === USER_TYPES.REPRESENTANTE_ORGANIZACAO;
  const isMigrante = userType === USER_TYPES.MIGRANTE_REFUGIADO;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUserTypeChange = (newType) => {
    setUserType(newType);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedFormData = {
      ...formData,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      cnpj: formData.cnpj.trim(),
      institutionName: formData.institutionName.trim(),
      nationality: formData.nationality.trim(),
      country: formData.country.trim(),
      state: formData.state.trim(),
      city: formData.city.trim(),
      neighborhood: formData.neighborhood.trim(),
      email: formData.email.trim(),
    };

    setFormData(trimmedFormData);

    let isFormValid = trimmedFormData.email && formData.password && formData.termsAgreed;

    if (isInstitution) {
      isFormValid = isFormValid
        && trimmedFormData.cnpj
        && trimmedFormData.institutionName
        && trimmedFormData.firstName
        && trimmedFormData.lastName;
    } else if (isMigrante) {
      isFormValid = isFormValid
        && trimmedFormData.firstName
        && trimmedFormData.lastName
        && trimmedFormData.nationality
        && trimmedFormData.situation;
    } else {
      isFormValid = isFormValid
        && trimmedFormData.firstName
        && trimmedFormData.lastName;
    }

    if (!isFormValid) {
      setMessageContent({ title: t('register.title'), message: t('register.errors.fillAllFields') });
      setShowMessage(true);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, trimmedFormData.email, formData.password);
      const user = userCredential.user;

      let userProfile;
      if (isInstitution) {
        userProfile = {
          firstName: trimmedFormData.firstName,
          lastName: trimmedFormData.lastName,
          cnpj: trimmedFormData.cnpj,
          institutionName: trimmedFormData.institutionName,
          email: trimmedFormData.email,
          type: 'institution',
          userType: USER_TYPES.REPRESENTANTE_ORGANIZACAO,
          status: 'ativo',
          termsAgreed: formData.termsAgreed,
          createdAt: serverTimestamp(),
        };
      } else if (isMigrante) {
        userProfile = {
          firstName: trimmedFormData.firstName,
          lastName: trimmedFormData.lastName,
          nationality: trimmedFormData.nationality,
          situation: trimmedFormData.situation,
          email: trimmedFormData.email,
          type: 'individual',
          userType: USER_TYPES.MIGRANTE_REFUGIADO,
          status: 'ativo',
          termsAgreed: formData.termsAgreed,
          createdAt: serverTimestamp(),
        };
      } else {
        userProfile = {
          firstName: trimmedFormData.firstName,
          lastName: trimmedFormData.lastName,
          email: trimmedFormData.email,
          type: 'individual',
          userType: USER_TYPES.APOIADOR,
          status: 'ativo',
          termsAgreed: formData.termsAgreed,
          createdAt: serverTimestamp(),
        };
      }

      // Nacionalidade e localização valem para todos os tipos de usuário.
      userProfile.address = {
        country: trimmedFormData.country,
        state: trimmedFormData.state,
        city: trimmedFormData.city,
        neighborhood: trimmedFormData.neighborhood,
      };
      if (trimmedFormData.nationality) {
        userProfile.nationality = trimmedFormData.nationality;
      }

      await setDoc(doc(db, 'users', user.uid), userProfile);

      if (isInstitution) {
        const cnpjClean = sanitizeCnpj(trimmedFormData.cnpj);
        // Procura uma organização existente com o mesmo CNPJ (já autenticado,
        // para respeitar as regras do Firestore). Vários representantes podem
        // compartilhar o mesmo CNPJ entrando como membros da mesma organização.
        const orgSnap = await getDocs(
          query(collection(db, 'organizations'), where('cnpj', '==', cnpjClean))
        );

        let organizationId;
        let memberRole;
        if (!orgSnap.empty) {
          organizationId = orgSnap.docs[0].id;
          memberRole = 'member';
        } else {
          const orgRef = await addDoc(collection(db, 'organizations'), {
            cnpj: cnpjClean,
            cnpjFormatted: trimmedFormData.cnpj,
            name: trimmedFormData.institutionName,
            ownerId: user.uid,
            createdAt: serverTimestamp(),
          });
          organizationId = orgRef.id;
          memberRole = 'owner';
        }

        await addDoc(collection(db, 'organizationMembers'), {
          organizationId,
          userId: user.uid,
          role: memberRole,
          status: 'active',
          createdAt: serverTimestamp(),
        });

        await setDoc(
          doc(db, 'users', user.uid),
          { organizationId, organizationRole: memberRole },
          { merge: true }
        );
      }

      navigate('/home-logado');
    } catch (error) {
      let errorMessage = t('register.errors.generic');
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = t('register.errors.emailInUse');
          break;
        case 'auth/invalid-email':
          errorMessage = t('register.errors.invalidEmail');
          break;
        case 'auth/weak-password':
          errorMessage = t('register.errors.weakPassword');
          break;
        default:
          console.error("Error code not handled!", error);
      }
      setMessageContent({ title: t('register.title'), message: errorMessage });
      setShowMessage(true);
    }
  };

  return (
    <div className="flex justify-center items-center bg-blue-600 bg-cover bg-no-repeat bg-center notranslate" style={{ backgroundImage: "url(/img/bg-cadastro.svg)" }}>
      <form onSubmit={handleSubmit} className="mt-20 mb-20 lg:w-1/2 s:w-full p-10 bg-white rounded-xl mx-4">
        <H2 text={t('register.title')} textColor="purple" />
        <div className="mt-4">
          <label className="block">
            <span className="text-lg text-gray-700">{t('register.userTypeQuestion')}</span>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-6">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="userType"
                  checked={userType === USER_TYPES.MIGRANTE_REFUGIADO}
                  onChange={() => handleUserTypeChange(USER_TYPES.MIGRANTE_REFUGIADO)}
                />
                <span className="ml-2 text-lg">{t('register.userType.migrante')}</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="userType"
                  checked={userType === USER_TYPES.REPRESENTANTE_ORGANIZACAO}
                  onChange={() => handleUserTypeChange(USER_TYPES.REPRESENTANTE_ORGANIZACAO)}
                />
                <span className="ml-2 text-lg">{t('register.userType.organization')}</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  className="form-radio"
                  name="userType"
                  checked={userType === USER_TYPES.APOIADOR}
                  onChange={() => handleUserTypeChange(USER_TYPES.APOIADOR)}
                />
                <span className="ml-2 text-lg">{t('register.userType.supporter')}</span>
              </label>
            </div>
            {isInstitution && <p className="mt-2 text-lg text-orange-700">{t('register.warningInstitution')}</p>}
          </label>
        </div>

        {isInstitution && (
          <div className="flex flex-col sm:flex-row sm:space-x-4">
            <div className="field sm:w-1/2">
              <label htmlFor="cnpj" className="block mt-4 text-lg">{t('register.fields.cnpj')}</label>
              <input id="cnpj" name="cnpj" type="text" value={formData.cnpj} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
            </div>
            <div className="field sm:w-1/2">
              <label htmlFor="institution-name" className="block mt-4 text-lg">{t('register.fields.institutionName')}</label>
              <input id="institution-name" name="institutionName" type="text" value={formData.institutionName} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:space-x-4">
          <div className="field sm:w-1/2">
            <label htmlFor="first-name" className="block mt-4 text-lg">
              {isInstitution ? t('register.fields.firstNameResponsible') : t('register.fields.firstName')}
            </label>
            <input id="first-name" name="firstName" type="text" value={formData.firstName} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
          <div className="field sm:w-1/2">
            <label htmlFor="last-name" className="block mt-4 text-lg">
              {isInstitution ? t('register.fields.lastNameResponsible') : t('register.fields.lastName')}
            </label>
            <input id="last-name" name="lastName" type="text" value={formData.lastName} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:space-x-4">
          <div className="field sm:w-1/2">
            <label htmlFor="nationality" className="block mt-4 text-lg">{t('register.fields.nationality')}</label>
            <input id="nationality" name="nationality" type="text" value={formData.nationality} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
          {isMigrante && (
            <div className="field sm:w-1/2">
              <label htmlFor="situation" className="block mt-4 text-lg">{t('register.fields.situation')}</label>
              <select id="situation" name="situation" value={formData.situation} className="w-full border border-gray-300 rounded-xl p-2 h-[42px]" onChange={handleInputChange}>
                <option value="">{t('register.situation.select')}</option>
                {SITUATION_KEYS.map((key) => (
                  <option key={key} value={key}>{t(`register.situation.${key}`)}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:space-x-4">
          <div className="field sm:w-1/2">
            <label htmlFor="country" className="block mt-4 text-lg">{t('register.fields.country')}</label>
            <input id="country" name="country" type="text" value={formData.country} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
          <div className="field sm:w-1/2">
            <label htmlFor="state" className="block mt-4 text-lg">{t('register.fields.state')}</label>
            <input id="state" name="state" type="text" value={formData.state} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:space-x-4">
          <div className="field sm:w-1/2">
            <label htmlFor="city" className="block mt-4 text-lg">{t('register.fields.city')}</label>
            <input id="city" name="city" type="text" value={formData.city} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
          <div className="field sm:w-1/2">
            <label htmlFor="neighborhood" className="block mt-4 text-lg">{t('register.fields.neighborhood')}</label>
            <input id="neighborhood" name="neighborhood" type="text" value={formData.neighborhood} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:space-x-4">
          <div className="field sm:w-1/2">
            <label htmlFor="email" className="block mt-4 text-lg">{t('register.fields.email')}</label>
            <input id="email" name="email" type="email" value={formData.email} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
          <div className="field sm:w-1/2">
            <label htmlFor="password" className="block mt-4 text-lg">{t('register.fields.password')}</label>
            <input id="password" name="password" type="password" value={formData.password} className="w-full border border-gray-300 rounded-xl p-2" onChange={handleInputChange} />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-center mt-10">
          <label htmlFor="terms" className="flex items-center text-lg mb-2 sm:mb-10">
            <input type="checkbox" id="terms" name="termsAgreed" checked={formData.termsAgreed} className="mr-2" onChange={(e) => setFormData({ ...formData, termsAgreed: e.target.checked })} />
            {t('register.termsAgreement')}{' '}
            <a href="/termos-de-uso" target="_blank" className="text-blue-800 text-lg hover:underline ml-1" rel="noopener noreferrer">{t('register.termsLink')}</a>
          </label>
          <Button text={isInstitution ? t('register.submitInstitution') : t('register.submit')} variant="transparent" className="w-full sm:w-auto" type="submit" />
        </div>
      </form>
      {showMessage && (
        <MessagePopup
          title={messageContent.title}
          message={messageContent.message}
          onClose={() => setShowMessage(false)}
        />
      )}
    </div>
  );
};

export default AccountCreationForm;
